import {
  PLAN_TIER_BY_TITLE,
  PLAN_TIER_BY_VARIANT_ID,
  PLAN_TIER_RANK,
  type PlanTier,
} from "@/lib/authz/plans";

// Client-safe derivation of a subscriber's billing state from the
// `personal_subscription_data` blob (already on the auth payload). Mirrors the
// date math in src/lib/server/sealSubscriptions.ts so the displayed
// renewal/access-end date matches the server's notion of expiry.

type MatchedSub = {
  status?: string | null;
  billingInterval?: string | null;
  deliveryInterval?: string | null;
  cancelledOn?: string | null;
  orderPlaced?: string | null;
  itemVariantIds?: unknown;
  itemTitles?: unknown;
  accessActive?: unknown;
};

export type SubscriptionSummary = {
  state: "active" | "cancelled" | "unknown";
  /** "monthly" | "annually" | `every N units` | null */
  billingLabel: string | null;
  /** Active → next renewal / period end; cancelled → access-end date. */
  date: Date | null;
};

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addInterval(start: Date, interval?: string | null): Date | null {
  if (!interval) return null;
  const match = interval.trim().match(/^(\d+)\s+(day|days|week|weeks|month|months|year|years)$/i);
  if (!match) return null;
  const count = Number(match[1]);
  const unit = match[2].toLowerCase();
  const next = new Date(start);
  if (unit.startsWith("day")) next.setDate(next.getDate() + count);
  else if (unit.startsWith("week")) next.setDate(next.getDate() + count * 7);
  else if (unit.startsWith("month")) next.setMonth(next.getMonth() + count);
  else if (unit.startsWith("year")) next.setFullYear(next.getFullYear() + count);
  else return null;
  return next;
}

// For an ACTIVE sub, `orderPlaced` is the ORIGINAL order date (Seal doesn't
// advance it per cycle), so `orderPlaced + interval` can be in the past for a
// long-running sub. Roll forward to the next future cycle so we display the
// real upcoming renewal date, never a stale past one.
function nextRenewalFrom(start: Date, interval?: string | null): Date | null {
  let date = addInterval(start, interval);
  if (!date) return null;
  const now = Date.now();
  for (let guard = 0; date && date.getTime() <= now && guard < 600; guard += 1) {
    const next = addInterval(date, interval);
    if (!next) break;
    date = next;
  }
  return date;
}

function billingLabel(interval?: string | null): string | null {
  if (!interval) return null;
  const match = interval.trim().match(/^(\d+)\s+(day|days|week|weeks|month|months|year|years)$/i);
  if (!match) return null;
  const count = Number(match[1]);
  const unit = match[2].toLowerCase();
  if (unit.startsWith("year") || (unit.startsWith("month") && count === 12)) return "annually";
  if (unit.startsWith("month") && count === 1) return "monthly";
  return `every ${count} ${unit}`;
}

function tierForSub(sub: MatchedSub): PlanTier {
  let best: PlanTier | null = null;
  const variantIds = Array.isArray(sub.itemVariantIds) ? sub.itemVariantIds : [];
  for (const variantId of variantIds) {
    if (typeof variantId !== "string") continue;
    const tier = PLAN_TIER_BY_VARIANT_ID[variantId];
    if (tier && (!best || PLAN_TIER_RANK[tier] > PLAN_TIER_RANK[best])) best = tier;
  }
  const titles = Array.isArray(sub.itemTitles) ? sub.itemTitles : [];
  for (const title of titles) {
    if (typeof title !== "string") continue;
    const tier = PLAN_TIER_BY_TITLE[title];
    if (tier && (!best || PLAN_TIER_RANK[tier] > PLAN_TIER_RANK[best])) best = tier;
  }
  return best ?? "free";
}

export function resolveSubscriptionSummary(
  data: Record<string, unknown> | null | undefined,
): SubscriptionSummary | null {
  if (!data || typeof data !== "object") return null;
  const matched = (data as { matchedSubscriptions?: MatchedSub[] }).matchedSubscriptions;
  if (!Array.isArray(matched) || matched.length === 0) return null;

  // The highest-tier subscription that currently grants access is the one whose
  // billing state the user cares about.
  let chosen: MatchedSub | null = null;
  let chosenRank = -1;
  for (const sub of matched) {
    if (sub.accessActive === false) continue;
    const rank = PLAN_TIER_RANK[tierForSub(sub)];
    if (rank > chosenRank) {
      chosen = sub;
      chosenRank = rank;
    }
  }
  if (!chosen) return null;

  const status = (chosen.status ?? "").trim().toUpperCase();
  const interval = chosen.billingInterval ?? chosen.deliveryInterval ?? null;
  const label = billingLabel(interval);

  if (status === "CANCELLED") {
    const cancelledOn = parseDate(chosen.cancelledOn);
    return { state: "cancelled", billingLabel: label, date: cancelledOn ? addInterval(cancelledOn, interval) : null };
  }
  if (status === "ACTIVE") {
    const orderPlaced = parseDate(chosen.orderPlaced);
    return { state: "active", billingLabel: label, date: orderPlaced ? nextRenewalFrom(orderPlaced, interval) : null };
  }
  return { state: "unknown", billingLabel: label, date: null };
}
